import { capture, initPostHog } from "./posthog";

export type ProductProfileType = "personal" | "terreiro";
export type ProductTriggerType = "ui" | "mutation";

export type ProductPageKey = string;

export type ProductBaseProperties = {
  pageKey: ProductPageKey;
  routePath: string;
  sourcePage: string;
  productArea?: string;
  activeProfileType?: ProductProfileType;
  terreiroId?: string;
  mediumId?: string;
  roleSlugs?: string[];
  success?: boolean;
  errorCode?: string | null;
  trigger?: ProductTriggerType;
  featureLimitBlocked?: boolean;
};

const isProductAnalyticsEnabled = (): boolean =>
  (import.meta.env?.VITE_POSTHOG_PRODUCT_ENABLED as string | undefined) !==
  "false";

const MAX_ERROR_CODE_LENGTH = 120;

export const sanitizeProductErrorCode = (value: unknown): string | null => {
  if (!value) return null;

  const raw =
    typeof value === "string"
      ? value
      : value instanceof Error
        ? value.message
        : String(value);

  const compact = raw.trim().replace(/\s+/g, "_").toLowerCase();
  if (!compact) return null;

  return compact.slice(0, MAX_ERROR_CODE_LENGTH);
};

const getActiveProfileType = (
  activeProfileType: ProductProfileType | undefined,
  terreiroId: string | undefined,
): ProductProfileType => {
  if (activeProfileType) return activeProfileType;
  return terreiroId ? "terreiro" : "personal";
};

export const buildProductBaseProperties = (
  props: ProductBaseProperties,
  flow = "product",
): Record<string, unknown> => ({
  flow,
  product_event_version: 1,
  ts_iso: new Date().toISOString(),
  page_key: props.pageKey,
  route_path: props.routePath,
  source_page: props.sourcePage,
  product_area: props.productArea ?? null,
  active_profile_type: getActiveProfileType(
    props.activeProfileType,
    props.terreiroId,
  ),
  terreiro_id: props.terreiroId ?? null,
  medium_id: props.mediumId ?? null,
  role_slugs: props.roleSlugs ?? null,
  success: props.success ?? true,
  error_code: props.errorCode ?? null,
  trigger: props.trigger ?? "ui",
  feature_limit_blocked: props.featureLimitBlocked ?? false,
});

const trackProductCapture = (
  event: string,
  baseProperties: ProductBaseProperties,
  eventProperties?: Record<string, unknown>,
  flow = "product",
): void => {
  if (!isProductAnalyticsEnabled()) return;

  initPostHog();
  capture(event, {
    ...buildProductBaseProperties(baseProperties, flow),
    ...(eventProperties ?? {}),
  });
};

export const trackProductPageView = (
  baseProperties: ProductBaseProperties,
  eventProperties?: Record<string, unknown>,
): void => {
  trackProductCapture("product_page_viewed", baseProperties, eventProperties);
};

export const trackProductEvent = (
  event: string,
  baseProperties: ProductBaseProperties,
  eventProperties?: Record<string, unknown>,
  flow = "product",
): void => {
  trackProductCapture(event, baseProperties, eventProperties, flow);
};

export const trackProductError = (
  event: string,
  baseProperties: Omit<ProductBaseProperties, "success" | "errorCode">,
  error: unknown,
  eventProperties?: Record<string, unknown>,
  flow = "product",
): void => {
  trackProductEvent(
    event,
    {
      ...baseProperties,
      success: false,
      errorCode: sanitizeProductErrorCode(error),
    },
    eventProperties,
    flow,
  );
};

export const trackProductAccessDenied = (
  baseProperties: ProductBaseProperties,
  eventProperties: {
    reasonCode: string;
    requiredPermissions?: string | string[];
  },
): void => {
  trackProductEvent("product_access_denied", baseProperties, {
    reason_code: eventProperties.reasonCode,
    required_permissions: eventProperties.requiredPermissions ?? null,
  });
};
